import React from "react";
import {
  School,
  TrendingUp,
  Female,
  Person,
  Public,
  Science,
  WorkOutline,
  CalendarToday,
  AccountBalance,
  Business,
  SchoolOutlined,
} from "@mui/icons-material";

// API Service for Dashboard Data
const DashboardAPI = {
  // Base API URL
  baseURL: '/api/profile/skills-profile',

  // Helper method to make API calls
  async makeAPICall(endpoint, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${this.baseURL}${endpoint}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching data from ${endpoint}:`, error);
      // Return fallback data or rethrow based on your error handling strategy
      throw error;
    }
  },

  // Helper method to get icon components
  getIconComponent(iconType) {
    const iconMap = {
      'school': <SchoolOutlined />,
      'trending-up': <TrendingUp />,
      'female': <Female />,
      'person': <Person />,
      'public': <Public />,
      'science': <Science />,
      'work': <WorkOutline />,
      'calendar': <CalendarToday />,
      'account-balance': <AccountBalance />,
      'business': <Business />,
    };
    return iconMap[iconType] || <SchoolOutlined />;
  },

  // Main method to fetch all dashboard data
  async fetchDashboardData(filters = {}) {
    try {
      // Make parallel API calls for better performance
      const [
        kpiGraduateData,
        kpiEmploymentData,
        educationLevelData,
        countryStudyData,
        employmentByFieldData,
        graduatesByGender,
        youthGraduatesByGender,
        genderEmploymentData,
        genderUnemploymentData,
        ageDistributionData,
        genderByFieldData,
        generalChartData,
        entryExitData,
      ] = await Promise.all([
        this.getKPIGraduateData(filters),
        this.getKPIEmploymentData(filters),
        this.getEducationLevelData(filters),
        this.getCountryStudyData(filters),
        this.getEmploymentByFieldData(filters),
        this.getGraduatesByGender(filters),
        this.getYouthGraduatesByGender(filters),
        this.getGenderEmploymentData(filters),
        this.getGenderUnemploymentData(filters),
        this.getAgeDistributionData(filters),
        this.getGenderByFieldData(filters),
        this.getGeneralChartData(filters),
        this.getEntryExitData(filters),
      ]);

      return {
        kpiGraduateData,
        kpiEmploymentData,
        educationLevelData,
        countryStudyData,
        employmentByFieldData,
        graduatesByGender,
        youthGraduatesByGender,
        genderEmploymentData,
        genderUnemploymentData,
        ageDistributionData,
        genderByFieldData,
        generalChartData,
        entryExitData,
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Return fallback data or handle error appropriately
      throw error;
    }
  },

  async getKPIGraduateData(filters) {
    try {
   //   const data = await this.makeAPICall('/kpi-graduate', filters);
      
      // Transform backend data to frontend format
      // return data.map(item => ({
      //   title: item.title,
      //   value: item.value,
      //   change: item.change,
      //   trend: item.trend,
      //   icon: this.getIconComponent(item.iconType),
      //   color: item.color,
      // }));
    } catch (error) {
      // console.error('Error fetching KPI Graduate data:', error);
      // Return fallback data
      // return [
      //   {
      //     title: "Total Graduates",
      //     value: "45,230",
      //     change: "+5%",
      //     trend: "up",
      //     icon: <SchoolOutlined />,
      //     color: "#1976d2",
      //   },
      //   {
      //     title: "Employment Rate",
      //     value: "72%",
      //     change: "+3%",
      //     trend: "up",
      //     icon: <TrendingUp />,
      //     color: "#388e3c",
      //   },
      //   {
      //     title: "Female Participation",
      //     value: "48%",
      //     change: "0%",
      //     trend: "stable",
      //     icon: <Female />,
      //     color: "#f57c00",
      //   },
      //   {
      //     title: "Youth Participation",
      //     value: "22%",
      //     change: "-4%",
      //     trend: "down",
      //     icon: <Person />,
      //     color: "#d32f2f",
      //   },
      //   {
      //     title: "Studied Abroad",
      //     value: "14%",
      //     change: "+2%",
      //     trend: "up",
      //     icon: <Public />,
      //     color: "#7b1fa2",
      //   },
      //   {
      //     title: "Top Field (ICT)",
      //     value: "24%",
      //     change: "0%",
      //     trend: "stable",
      //     icon: <Science />,
      //     color: "#5d4037",
      //   },
      // ];
    }
  },

  async getKPIEmploymentData(filters) {
    try {
      const data = await this.makeAPICall('/kpi-employment', filters);
      
      return data.map(item => ({
        title: item.title,
        value: item.value,
        change: item.change,
        trend: item.trend,
        icon: this.getIconComponent(item.iconType),
        color: item.color,
        description: item.description,
      }));
    } catch (error) {
      console.error('Error fetching KPI Employment data:', error);
      return [
        {
          title: "Total Employment",
          value: "45,230",
          change: "+5%",
          trend: "up",
          icon: <WorkOutline />,
          color: "#1976d2",
          description: "Overall workforce size in the public sector",
        },
        {
          title: "Above 60",
          value: "72%",
          change: "+3%",
          trend: "up",
          icon: <CalendarToday />,
          color: "#388e3c",
        },
        {
          title: "Female Employed",
          value: "48%",
          change: "0%",
          trend: "stable",
          icon: <Female />,
          color: "#f57c00",
          description: "Percentage of female employees in the workforce",
        },
        {
          title: "Youth Employed",
          value: "22%",
          change: "-4%",
          trend: "down",
          icon: <Person />,
          color: "#d32f2f",
          description: "Percentage of young employees (under 30)",
        },
        {
          title: "Permanent Staff",
          value: "86%",
          change: "+2%",
          trend: "up",
          icon: <AccountBalance />,
          color: "#7b1fa2",
          description: "Percentage of permanent vs temporary employees",
        },
        {
          title: "Top Sector (ICT)",
          value: "24%",
          change: "0%",
          trend: "stable",
          icon: <Business />,
          color: "#5d4037",
          description: "Leading sector by employment numbers",
        },
      ];
    }
  },

  async getEducationLevelData(filters) {
    try {
      return await this.makeAPICall('/education-level', filters);
    } catch (error) {
      console.error('Error fetching Education Level data:', error);
      return [
        {
          level: "Bachelor's",
          graduates: 0,
          employmentRate: 0,
          color: "#1976d2",
        },
        {
          level: "Master's",
          graduates: 0,
          employmentRate: 0,
          color: "#388e3c",
        },
        { level: "PhD", graduates: 0, employmentRate: 0, color: "#f57c00" },
        {
          level: "Advanced Diploma",
          graduates: 0,
          employmentRate: 0,
          color: "#7b1fa2",
        },
        {
          level: "Certificate/TVET",
          graduates: 0,
          employmentRate: 0,
          color: "#d32f2f",
        },
      ];
    }
  },

  async getCountryStudyData(filters) {
    try {
      return await this.makeAPICall('/country-study', filters);
    } catch (error) {
      console.error('Error fetching Country Study data:', error);
      return [
        { country: "-", graduates: 0, employmentRate: 0, color: "#1976d2" },
        { country: "-", graduates: 0, employmentRate: 0, color: "#388e3c" },
        { country: "-", graduates: 0, employmentRate: 0, color: "#f57c00" },
        { country: "-", graduates: 0, employmentRate: 0, color: "#7b1fa2" },
        { country: "-", graduates: 0, employmentRate: 0, color: "#5d4037" },
      ];
    }
  },

  async getEmploymentByFieldData(filters) {
    try {
      return await this.makeAPICall('/employment-by-field', filters);
    } catch (error) {
      console.error('Error fetching Employment by Field data:', error);
      return [
        {
          field: "Medicine & Health",
          graduates: 0,
          employed: 0,
          color: "#d32f2f",
        },
        { field: "ICT", graduates: 0, employed: 0, color: "#1976d2" },
        { field: "Engineering", graduates: 0, employed: 0, color: "#388e3c" },
        {
          field: "Business & Accounting",
          graduates: 0,
          employed: 0,
          color: "#f57c00",
        },
        { field: "Economics", graduates: 0, employed: 0, color: "#7b1fa2" },
        { field: "Law", graduates: 0, employed: 0, color: "#5d4037" },
        { field: "Agriculture", graduates: 0, employed: 0, color: "#8bc34a" },
        { field: "Education", graduates: 0, employed: 0, color: "#ff9800" },
      ];
    }
  },

  async getGraduatesByGender(filters) {
    // try {
    //   return await this.makeAPICall('/graduates-by-gender', filters);
    // } catch (error) {
    //   console.error('Error fetching Graduates by Gender data:', error);
    //   return [
    //     { gender: "Male", graduates: 23000, rate: 52 },
    //     { gender: "Female", graduates: 22230, rate: 48 },
    //   ];
    // }
  },

  async getYouthGraduatesByGender(filters) {
    // try {
    //   return await this.makeAPICall('/youth-graduates-by-gender', filters);
    // } catch (error) {
    //   console.error('Error fetching Youth Graduates by Gender data:', error);
    //   return [
    //     { gender: "Male", graduates: 5800, rate: 54 },
    //     { gender: "Female", graduates: 4900, rate: 46 },
    //   ];
    // }
  },

  async getGenderEmploymentData(filters) {
    // try {
    //   return await this.makeAPICall('/gender-employment', filters);
    // } catch (error) {
    //   console.error('Error fetching Gender Employment data:', error);
    //   return [
    //     { gender: "Male", employed: 16200, rate: 79 },
    //     { gender: "Female", employed: 13600, rate: 69 },
    //   ];
    // }
  },

  async getGenderUnemploymentData(filters) {
    // try {
    //   return await this.makeAPICall('/gender-unemployment', filters);
    // } catch (error) {
    //   console.error('Error fetching Gender Unemployment data:', error);
    //   return [
    //     { gender: "Male", unemployed: 4300, rate: 21 },
    //     { gender: "Female", unemployed: 6100, rate: 31 },
    //   ];
    // }
  },

  async getAgeDistributionData(filters) {
    try {
      return await this.makeAPICall('/age-distribution', filters);
    } catch (error) {
      console.error('Error fetching Age Distribution data:', error);
      return [
        { age: "20-24", graduates: 0, employed: 0, color: "#1976d2" },
        { age: "25-29", graduates: 0, employed: 0, color: "#388e3c" },
        { age: "30-39", graduates: 0, employed: 0, color: "#f57c00" },
        { age: "40+", graduates: 0, employed: 0, color: "#7b1fa2" },
      ];
    }
  },
  async getGenderByFieldData(filters) {
    try {
      return await this.makeAPICall('/gender-by-field', filters);
    } catch (error) {
      console.error('Error fetching Gender by Field data:', error);
      return [
        { field: "Health Sciences", female: 0, male: 0, color: "#d32f2f" },
        { field: "Education", female: 0, male: 0, color: "#f57c00" },
        { field: "Business", female: 0, male: 0, color: "#388e3c" },
        { field: "STEM (ICT/Eng)", female: 0, male: 0, color: "#1976d2" },
      ];
    }
  },

  async getGeneralChartData(filters) {
    try {
      return await this.makeAPICall('/general-charts', filters);
    } catch (error) {
      console.error('Error fetching General Chart data:', error);
      // Return fallback data matching the provided structure
      return [
        { chartType: "gender", label: "Female", value: 0, valuePercent: 0.00 },
        { chartType: "gender", label: "Male", value: 0, valuePercent: 0.00 },
        { chartType: "studyLocation", label: "Study Abroad", value: 0, valuePercent: 0 },
        { chartType: "studyLocation", label: "Rwanda", value: 0, valuePercent: 0 },
        { chartType: "studyLocation", label: "Unspecified", value: 0, valuePercent: 0 },
        { chartType: "youthByGender", label: "Female Youth", value: 0, valuePercent: 0 },
        { chartType: "youthByGender", label: "Male Youth", value: 0, valuePercent: 0 },
        { chartType: "youthSummary", label: "Youth (16–30)", value: 0, valuePercent: 20 },
        { chartType: "youthSummary", label: "All (16+)", value: 0, valuePercent: 0 },
      ];
    }
  },

  async getNewHiresTrend(filters = {}) {
  try {
    return await this.makeAPICall("/new-hires-trend", filters);
  } catch (error) {
    console.error("Error fetching New Hires Trend data:", error);
    return [];
  }
},

async getExitsTrend(filters = {}) {
  try {
    return await this.makeAPICall("/exits-trend", filters);
  } catch (error) {
    console.error("Error fetching Exits Trend data:", error);
    return [];
  }
},


  async getEntryExitData(filters) {
    try {
      return await this.makeAPICall('/entry-exit-by-field', filters);
    } catch (error) {
      console.error('Error fetching Entry/Exit data:', error);
      // Return fallback data in tall format: { year, field, entry, exit }
      return [
        { year: '2020', field: '-', entry: 0, exit: 0 },
        { year: '2020', field: '-', entry: 0, exit: 0 },
        { year: '2020', field: '-', entry: 0, exit: 0 },
        { year: '2020', field: '-', entry: 0, exit: 0 },
        { year: '2020', field: '-', entry: 0, exit: 0 },
        { year: '2020', field: '-', entry: 0, exit: 0 },
        { year: '2021', field: '-', entry: 0, exit: 0 },
        { year: '2021', field: '-', entry: 0, exit: 0 },
        { year: '2021', field: '-', entry: 0, exit: 0 },
        { year: '2021', field: '-', entry: 0, exit: 0 },
        { year: '2021', field: '-', entry: 0, exit: 0 },
        { year: '2021', field: '-', entry: 0, exit: 0 },
        { year: '2022', field: '-', entry: 0, exit: 0 },
        { year: '2022', field: '-', entry: 0, exit: 0 },
        { year: '2022', field: '-', entry: 0, exit: 0 },
        { year: '2022', field: '-', entry: 0, exit: 0 },
        { year: '2022', field: '-', entry: 0, exit: 0 },
        { year: '2022', field: '-', entry: 0, exit: 0 },
        { year: '2023', field: '-', entry: 0, exit: 0 },
        { year: '2023', field: '-', entry: 0, exit: 0 },
        { year: '2023', field: '-', entry: 0, exit: 0 },
        { year: '2023', field: '-', entry: 0, exit: 0 },
        { year: '2023', field: '-', entry: 0, exit: 0 },
        { year: '2023', field: '-', entry: 0, exit: 0 },
        { year: '2024', field: '-', entry: 0, exit: 0 },
        { year: '2024', field: '-', entry: 0, exit: 0 },
        { year: '2024', field: '-', entry: 0, exit: 0 },
        { year: '2024', field: '-', entry: 0, exit: 0 },
        { year: '2024', field: '-', entry: 0, exit: 0 },
        { year: '2024', field: '-', entry: 0, exit: 0 },
        { year: '2025', field: '-', entry: 0, exit: 0 },
        { year: '2025', field: '-', entry: 0, exit: 0 },
        { year: '2025', field: '-', entry: 0, exit: 0 },
        { year: '2025', field: '-', entry: 0, exit: 0 },
        { year: '2025', field: '-', entry: 0, exit: 0 },
        { year: '2025', field: '-', entry: 0, exit: 0 },
      ];
    }
  },
};



export default DashboardAPI;

